/* ==========================================================================
   ADMATI — CUSTOM JS
   All our own behaviour lives here in ONE place, so it is easy to re-apply
   after a theme version update. Loaded from layout/theme.liquid, right after
   the <script type="application/json" id="admati-qty-limit-data"> bridge.
   Keep this file additive only — never paste theme code here.

   FEATURE: per-product quantity limit ("max 2 kg of walnuts per order")
   --------------------------------------------------------------------------
   The limit is a product metafield (custom.max_qty_grams). Pack size per
   variant comes from Shopify's native unit-price field, so 10 x 200g and
   5 x 400g both correctly add up to 2000g.

   Enforcement is a single wrapper around window.fetch that inspects every
   /cart/add.js, /cart/change.js and /cart/update.js request. That is the one
   choke point every add-to-cart path in the theme goes through — product page,
   collection cards, quick view, upsell, "bought together", re-order, bundles —
   so we never have to touch the 12 snippets that render those buttons.

   Failure policy: this must never break the store. Every entry point is
   wrapped in try/catch and falls back to the untouched original request.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__admatiCustomLoaded) return;
  window.__admatiCustomLoaded = true;

  var origFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!origFetch) return;

  /* ---------------------------------------------------------------------- */
  /* Config + registry                                                       */
  /* ---------------------------------------------------------------------- */

  var CFG = { enabled: false, text: {}, products: [] };
  var BY_HANDLE = Object.create(null);   // handle    -> entry
  var BY_VARIANT = Object.create(null);  // variantId -> handle
  var PENDING = Object.create(null);     // handle    -> in-flight fetch promise

  /* Variant swaps in the cart (assets/cart-variant-picker.js) cannot change a
     line's variant in place — Shopify has no such endpoint — so they add the
     new variant and THEN delete the old line. For that brief moment the cart
     holds both, and the old line would eat the allowance the new one needs.
     The picker announces the line it is replacing, and we leave it out of the
     sums until the swap finishes. */
  var swapKey = null;
  var blockedCount = 0;   // bumped whenever an add is refused outright

  window.AdmatiQtyLimit = {
    beginSwap: function (key) { swapKey = key == null ? null : String(key); },
    endSwap: function () { swapKey = null; },
    getBlockedCount: function () { return blockedCount; },
    // Human-readable limit for a variant ("2 ק"ג"), so callers can name the
    // actual number in their own message instead of a vague "not allowed".
    getLimitLabel: function (variantId) {
      var entry = entryForVariant(variantId);
      return entry && entry.limit ? formatAmount(entry, entry.limit) : '';
    },

    // Show a message in the same floating toast the limit uses everywhere else,
    // so every "over the limit" moment on the site looks and behaves alike.
    notify: function (message) { showToast(message); },

    /* Answers "would this variant, at this quantity, still fit?" for one cart
       line — the line itself excluded, since it is the one being replaced.
       Resolves ONCE with a synchronous checker so a whole <select> can be
       annotated without a request per option. Any failure resolves to a checker
       that approves everything: a broken hint must never block a real change. */
    getFitChecker: function () {
      return getCart()
        .then(function (cart) {
          if (!cart || !cart.items) return null;
          var handles = [];
          cart.items.forEach(function (l) {
            if (l.handle && handles.indexOf(l.handle) === -1) handles.push(l.handle);
          });
          return Promise.all(handles.map(ensureProduct)).then(function () { return cart; });
        })
        .then(function (cart) {
          if (!cart) return function () { return { ok: true }; };
          return function (lineKey, variantId, qty) {
            var entry = entryForVariant(variantId);
            if (!entry || !entry.limit) return { ok: true };
            var size = packSize(entry, variantId);
            if (size <= 0) return { ok: true };

            var others = 0;
            for (var i = 0; i < cart.items.length; i++) {
              var line = cart.items[i];
              if (String(line.product_id) !== String(entry.id)) continue;
              if (String(line.key) === String(lineKey)) continue;
              var s = packSize(entry, line.variant_id);
              if (s > 0) others += s * line.quantity;
            }

            var total = others + size * (qty || 1);
            if (total <= entry.limit) return { ok: true };
            return {
              ok: false,
              limitLabel: formatAmount(entry, entry.limit),
              totalLabel: formatAmount(entry, total)
            };
          };
        })
        .catch(function () { return function () { return { ok: true }; }; });
    }
  };

  function register(entry) {
    if (!entry || !entry.handle) return;
    BY_HANDLE[entry.handle] = entry;
    var packs = entry.packs || {};
    for (var id in packs) {
      if (Object.prototype.hasOwnProperty.call(packs, id)) BY_VARIANT[id] = entry.handle;
    }
  }

  function boot() {
    var el = document.getElementById('admati-qty-limit-data');
    if (!el) return;
    try {
      var parsed = JSON.parse(el.textContent || el.innerHTML);
      CFG.enabled = !!parsed.enabled;
      CFG.text = parsed.text || {};
      (parsed.products || []).forEach(register);
    } catch (e) {
      CFG.enabled = false;
    }
  }

  /* Fetch a product's limit data on demand (quick-add straight from a card).
     `null` results are cached too, so an unlimited product is asked for once. */
  function ensureProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (Object.prototype.hasOwnProperty.call(BY_HANDLE, handle)) return Promise.resolve(BY_HANDLE[handle]);
    if (PENDING[handle]) return PENDING[handle];

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    PENDING[handle] = origFetch(root + 'products/' + encodeURIComponent(handle) + '?view=qty-limit', {
      headers: { Accept: 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (r) { return r.text(); })
      .then(function (t) {
        var entry = null;
        try { entry = JSON.parse((t || '').trim()); } catch (e) { entry = null; }
        if (entry && entry.handle) register(entry);
        else BY_HANDLE[handle] = null;
        return BY_HANDLE[handle];
      })
      .catch(function () { BY_HANDLE[handle] = null; return null; })
      .then(function (v) { delete PENDING[handle]; return v; });

    return PENDING[handle];
  }

  function entryForVariant(variantId) {
    var handle = BY_VARIANT[String(variantId)];
    return handle ? BY_HANDLE[handle] : null;
  }

  /* Pack size, in the entry's own unit. "units" mode counts every item as 1. */
  function packSize(entry, variantId) {
    if (!entry) return 0;
    if (entry.mode === 'units') return 1;
    var g = (entry.packs || {})[String(variantId)];
    return typeof g === 'number' && g > 0 ? g : 0;
  }

  function formatAmount(entry, amount) {
    var t = CFG.text || {};
    if (!entry || entry.mode === 'units') return amount + ' ' + (t.units || 'יחידות');
    if (amount >= 1000) {
      var kg = amount / 1000;
      var pretty = Math.round(kg * 100) / 100;
      return pretty + ' ' + (t.kg || 'ק"ג');
    }
    return amount + ' ' + (t.gram || 'גרם');
  }

  function fillText(tpl, entry, added) {
    return String(tpl || '')
      .replace(/\[title\]/g, entry.title || '')
      .replace(/\[limit\]/g, formatAmount(entry, entry.limit))
      .replace(/\[added\]/g, formatAmount(entry, added || 0));
  }

  /* ---------------------------------------------------------------------- */
  /* Cart access                                                             */
  /* ---------------------------------------------------------------------- */

  function getCart() {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return origFetch(root + 'cart.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store'
    }).then(function (r) { return r.json(); }).catch(function () { return null; });
  }

  /* How much of this product is already in the cart, in the entry's unit.
     A line that is mid-swap is skipped: it is about to be deleted, so counting
     it would block the replacement it is being swapped for. */
  function amountInCart(cart, entry) {
    if (!cart || !cart.items || !entry) return 0;
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var line = cart.items[i];
      if (String(line.product_id) !== String(entry.id)) continue;
      if (swapKey && String(line.key) === swapKey) continue;
      var size = packSize(entry, line.variant_id);
      if (size > 0) total += size * line.quantity;
    }
    return total;
  }

  /* ---------------------------------------------------------------------- */
  /* Toast                                                                   */
  /* ---------------------------------------------------------------------- */

  var toastTimer = null;

  function showToast(message) {
    if (!message) return;
    try {
      var host = document.getElementById('admati-toast');
      if (!host) {
        host = document.createElement('div');
        host.id = 'admati-toast';
        host.className = 'admati-toast';
        host.setAttribute('role', 'status');
        host.setAttribute('aria-live', 'polite');
        document.body.appendChild(host);
      }
      host.textContent = message;
      host.classList.add('is-visible');
      if (toastTimer) clearTimeout(toastTimer);
      toastTimer = setTimeout(function () { host.classList.remove('is-visible'); }, 6000);
    } catch (e) { /* never break the store over a toast */ }
  }

  /* ---------------------------------------------------------------------- */
  /* Request body helpers                                                    */
  /* ---------------------------------------------------------------------- */

  function parseBody(body) {
    if (!body) return null;
    if (typeof body === 'string') {
      try { return { kind: 'json', data: JSON.parse(body) }; }
      catch (e) { return { kind: 'params', data: new URLSearchParams(body) }; }
    }
    if (typeof FormData !== 'undefined' && body instanceof FormData) return { kind: 'form', data: body };
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return { kind: 'params', data: body };
    return null;
  }

  function serialiseBody(parsed) {
    if (parsed.kind === 'json') return JSON.stringify(parsed.data);
    return parsed.data; // FormData / URLSearchParams are sent as-is
  }

  /* Normalise an add request into [{id, quantity}] regardless of body flavour. */
  function readAddItems(parsed) {
    if (!parsed) return [];
    if (parsed.kind === 'json') {
      var d = parsed.data;
      if (Array.isArray(d.items)) {
        return d.items.map(function (it) {
          return { id: String(it.id), quantity: parseInt(it.quantity, 10) || 1 };
        });
      }
      if (d.id) return [{ id: String(d.id), quantity: parseInt(d.quantity, 10) || 1 }];
      return [];
    }
    var id = parsed.data.get('id');
    if (!id) return [];
    return [{ id: String(id), quantity: parseInt(parsed.data.get('quantity'), 10) || 1 }];
  }

  function writeAddItems(parsed, items) {
    if (parsed.kind === 'json') {
      var d = parsed.data;
      if (Array.isArray(d.items)) {
        d.items = items.map(function (it) { return { id: it.id, quantity: it.quantity }; });
      } else if (items.length) {
        d.quantity = items[0].quantity;
      }
      return;
    }
    if (items.length) parsed.data.set('quantity', String(items[0].quantity));
  }

  function readSectionIds(parsed) {
    try {
      if (parsed && parsed.kind === 'json') {
        var s = parsed.data.sections;
        if (Array.isArray(s)) return s;
        if (typeof s === 'string') return s.split(',');
      } else if (parsed) {
        var raw = parsed.data.get('sections');
        if (raw) return String(raw).split(',');
      }
    } catch (e) { /* ignore */ }
    return [];
  }

  /* ---------------------------------------------------------------------- */
  /* /cart/add.js                                                            */
  /* ---------------------------------------------------------------------- */

  function handleAdd(url, init) {
    var parsed = parseBody(init && init.body);
    var items = readAddItems(parsed);
    if (!items.length) return origFetch(url, init);

    return ensureAll(items.map(function (i) { return i.id; }))
      .then(function () { return getCart(); })
      .then(function (cart) {
        var running = Object.create(null); // entry.handle -> amount claimed so far in THIS request
        var kept = [];
        var messages = [];

        items.forEach(function (item) {
          var entry = entryForVariant(item.id);
          var size = packSize(entry, item.id);

          // Unknown product, no limit, or a variant with no pack data: leave alone.
          if (!entry || !entry.limit || size <= 0) { kept.push(item); return; }

          var already = amountInCart(cart, entry) + (running[entry.handle] || 0);
          var room = entry.limit - already;

          if (room <= 0) {
            messages.push({ type: 'full', entry: entry, added: 0 });
            return; // drop this item entirely
          }

          var wanted = size * item.quantity;
          if (wanted <= room) {
            running[entry.handle] = (running[entry.handle] || 0) + wanted;
            kept.push(item);
            return;
          }

          var allowedQty = Math.floor(room / size);
          if (allowedQty <= 0) {
            messages.push({ type: 'full', entry: entry, added: 0 });
            return;
          }
          running[entry.handle] = (running[entry.handle] || 0) + allowedQty * size;
          messages.push({ type: 'trimmed', entry: entry, added: already + allowedQty * size });
          kept.push({ id: item.id, quantity: allowedQty });
        });

        if (!messages.length) return origFetch(url, init); // nothing to enforce

        // A variant swap has to be all-or-nothing. If we would trim or refuse
        // it, add nothing and report it: the picker checks this counter and
        // restores the customer's selection instead of deleting the old line.
        if (swapKey) {
          blockedCount++;
          return noopCartResponse(readSectionIds(parsed));
        }

        var first = messages[0];
        var text = first.type === 'full'
          ? fillText(CFG.text.full, first.entry, 0)
          : fillText(CFG.text.trimmed, first.entry, first.added);

        // Everything was rejected: don't add anything, but still give the theme
        // a real Shopify cart response (a no-op /cart/update.js) so the drawer
        // and the bubble re-render exactly as they normally would.
        if (!kept.length) {
          blockedCount++;
          return noopCartResponse(readSectionIds(parsed)).then(function (res) {
            showToast(text);
            return res;
          });
        }

        writeAddItems(parsed, kept);
        var newInit = Object.assign({}, init, { body: serialiseBody(parsed) });
        return origFetch(url, newInit).then(function (res) {
          showToast(text);
          return res;
        });
      })
      .catch(function () { return origFetch(url, init); });
  }

  function ensureAll(variantIds) {
    var unknown = variantIds.filter(function (id) { return !BY_VARIANT[String(id)]; });
    if (!unknown.length) return Promise.resolve();
    // A variant we've never seen: resolve its product handle through the cart
    // once it lands there, or through the page. Nothing to look up in advance,
    // so we let the add through and reconcile afterwards (see verifyAndClamp).
    return Promise.resolve();
  }

  function noopCartResponse(sectionIds) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var payload = { updates: {} };
    if (sectionIds && sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }
    return origFetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
  }

  /* ---------------------------------------------------------------------- */
  /* /cart/change.js — the +/- buttons and the quantity input in the cart    */
  /* ---------------------------------------------------------------------- */

  function handleChange(url, init) {
    var parsed = parseBody(init && init.body);
    if (!parsed || parsed.kind !== 'json') return origFetch(url, init);

    var d = parsed.data;
    var wantQty = parseInt(d.quantity, 10);
    if (!(wantQty > 0)) return origFetch(url, init); // removals always allowed

    return getCart().then(function (cart) {
      if (!cart || !cart.items) return origFetch(url, init);

      var line = null;
      if (d.line) line = cart.items[parseInt(d.line, 10) - 1];
      else if (d.id) {
        for (var i = 0; i < cart.items.length; i++) {
          if (String(cart.items[i].key) === String(d.id) || String(cart.items[i].variant_id) === String(d.id)) {
            line = cart.items[i];
            break;
          }
        }
      }
      if (!line) return origFetch(url, init);

      return ensureProduct(line.handle).then(function (entry) {
        if (!entry || !entry.limit) return origFetch(url, init);
        var size = packSize(entry, line.variant_id);
        if (size <= 0) return origFetch(url, init);

        // Everything of this product EXCEPT the line being changed.
        var others = 0;
        for (var i = 0; i < cart.items.length; i++) {
          var other = cart.items[i];
          if (String(other.product_id) !== String(entry.id)) continue;
          if (String(other.key) === String(line.key)) continue;
          var s = packSize(entry, other.variant_id);
          if (s > 0) others += s * other.quantity;
        }

        var room = entry.limit - others;
        var maxQty = Math.floor(room / size);
        if (maxQty < 0) maxQty = 0;
        if (wantQty <= maxQty) return origFetch(url, init);

        if (maxQty <= 0) {
          showToast(fillText(CFG.text.full, entry, 0));
        } else {
          showToast(fillText(CFG.text.trimmed, entry, others + maxQty * size));
        }
        d.quantity = maxQty > 0 ? maxQty : line.quantity;
        return origFetch(url, Object.assign({}, init, { body: JSON.stringify(d) }));
      });
    }).catch(function () { return origFetch(url, init); });
  }

  /* ---------------------------------------------------------------------- */
  /* Safety net: reconcile the cart after any add we could not pre-check      */
  /* ---------------------------------------------------------------------- */

  var verifyTimer = null;

  function scheduleVerify() {
    if (verifyTimer) clearTimeout(verifyTimer);
    verifyTimer = setTimeout(verifyAndClamp, 400);
  }

  function verifyAndClamp() {
    if (swapKey) return; // mid-swap the cart is deliberately inconsistent
    getCart().then(function (cart) {
      if (!cart || !cart.items || !cart.items.length) return;

      var handles = [];
      cart.items.forEach(function (line) {
        if (line.handle && handles.indexOf(line.handle) === -1) handles.push(line.handle);
      });

      return Promise.all(handles.map(ensureProduct)).then(function () {
        var updates = {};
        var message = null;

        handles.forEach(function (handle) {
          var entry = BY_HANDLE[handle];
          if (!entry || !entry.limit) return;

          var lines = cart.items.filter(function (l) { return String(l.product_id) === String(entry.id); });
          var total = 0;
          lines.forEach(function (l) {
            var s = packSize(entry, l.variant_id);
            if (s > 0) total += s * l.quantity;
          });
          if (total <= entry.limit) return;

          // Trim from the most recently added lines first.
          var excess = total - entry.limit;
          for (var i = lines.length - 1; i >= 0 && excess > 0; i--) {
            var l = lines[i];
            var s = packSize(entry, l.variant_id);
            if (s <= 0) continue;
            var drop = Math.min(l.quantity, Math.ceil(excess / s));
            updates[l.key] = l.quantity - drop;
            excess -= drop * s;
          }
          message = fillText(CFG.text.trimmed, entry, entry.limit);
        });

        if (!Object.keys(updates).length) return;
        return applyUpdates(updates).then(function () { showToast(message); });
      });
    }).catch(function () { /* silent */ });
  }

  function applyUpdates(updates) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    var payload = { updates: updates };
    var sectionIds = sectionsToRender();
    if (sectionIds.length) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }
    return origFetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        try {
          if (state && state.sections && window.Alpine && Alpine.store('xCartHelper')) {
            Alpine.store('xCartHelper').reRenderSections(state.sections);
          }
          document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
        } catch (e) { /* ignore */ }
        return state;
      });
  }

  function sectionsToRender() {
    try {
      if (window.Alpine && Alpine.store('xCartHelper') && Alpine.store('xCartHelper').getSectionsToRender) {
        return Alpine.store('xCartHelper').getSectionsToRender().map(function (s) { return s.id; });
      }
    } catch (e) { /* ignore */ }
    return [];
  }

  /* ---------------------------------------------------------------------- */
  /* Lock the "+" button once a line has reached the limit                   */
  /* ---------------------------------------------------------------------- */

  function refreshPlusButtons() {
    if (!CFG.enabled || swapKey) return;
    var inputs = document.querySelectorAll('input[id^="cart-qty-"]');
    if (!inputs.length) return;

    getCart().then(function (cart) {
      if (!cart || !cart.items) return;
      var handles = [];
      cart.items.forEach(function (l) {
        if (l.handle && handles.indexOf(l.handle) === -1) handles.push(l.handle);
      });
      return Promise.all(handles.map(ensureProduct)).then(function () {
        Array.prototype.forEach.call(inputs, function (input) {
          var key = input.id.replace('cart-qty-', '');
          var line = null;
          for (var i = 0; i < cart.items.length; i++) {
            if (String(cart.items[i].key) === key) { line = cart.items[i]; break; }
          }
          if (!line) return;
          var entry = BY_HANDLE[line.handle];
          if (!entry || !entry.limit) return;

          var total = 0;
          cart.items.forEach(function (l) {
            if (String(l.product_id) !== String(entry.id)) return;
            var s = packSize(entry, l.variant_id);
            if (s > 0) total += s * l.quantity;
          });

          var size = packSize(entry, line.variant_id);
          var atLimit = size > 0 && total + size > entry.limit;
          var plus = input.parentElement && input.parentElement.querySelector('button[aria-label="button-plus"]');
          if (plus) plus.classList.toggle('admati-qty-locked', atLimit);
          input.parentElement && input.parentElement.classList.toggle('admati-at-limit', atLimit);
        });
      });
    }).catch(function () { /* silent */ });
  }

  /* ---------------------------------------------------------------------- */
  /* Wire up                                                                 */
  /* ---------------------------------------------------------------------- */

  window.fetch = function (input, init) {
    try {
      if (CFG.enabled) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var hasBody = init && init.body;
        if (hasBody && /\/cart\/add(\.js)?(\?|$)/.test(url)) return handleAdd(input, init);
        if (hasBody && /\/cart\/change(\.js)?(\?|$)/.test(url)) return handleChange(input, init);
      }
    } catch (e) { /* fall through to the untouched request */ }
    return origFetch.apply(null, arguments);
  };

  // Read config immediately: this file is deferred, so the JSON bridge that
  // theme.liquid renders above it is already parsed, and the fetch wrapper must
  // know whether it is enabled before any interaction can fire a cart request.
  boot();

  function init() {
    if (!CFG.enabled) return;
    refreshPlusButtons();
    document.addEventListener('eurus:cart:items-changed', function () {
      scheduleVerify();
      setTimeout(refreshPlusButtons, 500);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
